import type {
  GeneratedImageDisplay,
  GeneratedSvgDisplay,
} from "@ando/core/domain";

/**
 * Resolve the ``content_items`` doc_id for an agent-generated image so the
 * working area can open it via ``?focus=<doc_id>``.
 *
 * Prefers the explicit ``doc_id`` from the BE payload (June 2026+); falls back
 * to deriving it from the image URL — the service builds both the URL
 * (``/api/v1/minio-images/generated/<id>.<ext>``) and the doc_id
 * (``generated_<id>``) from the same image id — so older payloads (pre-doc_id)
 * still open. Returns ``null`` when neither yields an id.
 */
export function generatedImageDocId(img: GeneratedImageDisplay): string | null {
  if (img.doc_id?.trim()) return img.doc_id;
  const m = img.image_url.match(/\/minio-images\/generated\/([^/]+?)\.[^./]+$/);
  return m ? `generated_${m[1]}` : null;
}

/**
 * Mirror of ``generatedImageDocId`` for SVG payloads. Both extractors share
 * the same URL → ``generated_<id>`` derivation because the BE
 * ``svg_generation_service`` registers the asset under the same naming
 * convention as the raster generator. Kept in this single module so a
 * future ``generated_audio`` / ``generated_video`` follows the same shape.
 */
export function generatedSvgDocId(svg: GeneratedSvgDisplay): string | null {
  if (svg.doc_id?.trim()) return svg.doc_id;
  const m = svg.svg_url.match(/\/minio-images\/generated\/([^/]+?)\.[^./]+$/);
  return m ? `generated_${m[1]}` : null;
}
